import type { InsightSection, KpiItem, MonthDayRecord, MonthWeekSummary } from './types';

/** May 20 2026 (Wed). Practice closed Sundays. */
export const MONTH_TODAY = { year: 2026, month: 4, day: 20 };

export const MONTH_GOAL = 22000;

/** Per-day production for May 2026. Sundays omitted = closed. */
export const MAY_DATA: Record<number, MonthDayRecord> = {
  1: { production: 25474, goal: 22000, visits: 54, newPatients: 4, noShows: 2, cancellations: 1 },
  2: { production: 18200, goal: 18000, visits: 38, newPatients: 2, noShows: 1, cancellations: 1 },
  4: { production: 19800, goal: 22000, visits: 42, newPatients: 3, noShows: 4, cancellations: 2 },
  5: { production: 22100, goal: 22000, visits: 48, newPatients: 5, noShows: 3, cancellations: 1 },
  6: { production: 24500, goal: 22000, visits: 52, newPatients: 6, noShows: 2, cancellations: 0 },
  7: { production: 26200, goal: 22000, visits: 56, newPatients: 4, noShows: 3, cancellations: 1 },
  8: { production: 25800, goal: 22000, visits: 54, newPatients: 5, noShows: 4, cancellations: 2 },
  9: { production: 19500, goal: 18000, visits: 40, newPatients: 3, noShows: 3, cancellations: 2 },
  11: {
    production: 11200,
    goal: 22000,
    visits: 28,
    newPatients: 2,
    noShows: 8,
    cancellations: 6,
    anomaly: 'Broken sterilizer',
  },
  12: {
    production: 8500,
    goal: 22000,
    visits: 20,
    newPatients: 1,
    noShows: 12,
    cancellations: 8,
    anomaly: 'Sterilizer · day 2',
  },
  13: { production: 24300, goal: 22000, visits: 51, newPatients: 5, noShows: 3, cancellations: 1 },
  14: { production: 26100, goal: 22000, visits: 55, newPatients: 6, noShows: 4, cancellations: 2 },
  15: { production: 25900, goal: 22000, visits: 53, newPatients: 4, noShows: 5, cancellations: 1 },
  16: { production: 20300, goal: 18000, visits: 41, newPatients: 3, noShows: 2, cancellations: 1 },
  18: { production: 22000, goal: 22000, visits: 46, newPatients: 5, noShows: 3, cancellations: 1 },
  19: { production: 24800, goal: 22000, visits: 52, newPatients: 6, noShows: 2, cancellations: 0 },
  20: {
    production: 11200,
    goal: 22000,
    visits: 24,
    newPatients: 1,
    noShows: 3,
    cancellations: 1,
    partial: true,
  },
  21: { production: 14800, goal: 22000, visits: 31, newPatients: 2, projected: true, noShows: 0, cancellations: 0 },
  22: { production: 5300, goal: 22000, visits: 12, newPatients: 0, projected: true, noShows: 0, cancellations: 0 },
  23: { production: 19200, goal: 18000, visits: 38, newPatients: 4, projected: true, noShows: 0, cancellations: 0 },
  25: { production: 12100, goal: 22000, visits: 26, newPatients: 1, projected: true, noShows: 0, cancellations: 0 },
  26: { production: 8900, goal: 22000, visits: 19, newPatients: 0, projected: true, noShows: 0, cancellations: 0 },
  27: { production: 24500, goal: 22000, visits: 50, newPatients: 4, projected: true, noShows: 0, cancellations: 0 },
  28: { production: 26000, goal: 22000, visits: 55, newPatients: 6, projected: true, noShows: 0, cancellations: 0 },
  29: { production: 25800, goal: 22000, visits: 53, newPatients: 5, projected: true, noShows: 0, cancellations: 0 },
  30: { production: 19200, goal: 18000, visits: 39, newPatients: 3, projected: true, noShows: 0, cancellations: 0 },
};

const monthGoal = 570000;

const mtd = Object.entries(MAY_DATA)
  .filter(([d, v]) => Number(d) <= MONTH_TODAY.day && !v.projected)
  .reduce((s, [, v]) => s + v.production, 0);

const projected = Object.values(MAY_DATA).reduce((s, v) => s + v.production, 0);

export const MONTH_KPIS: KpiItem[] = [
  {
    label: 'Projected · May',
    primary: `$${(projected / 1000).toFixed(0)}k`,
    sub: `of $570k goal · ${Math.round((projected / monthGoal) * 100)}%`,
    subTone: projected >= monthGoal ? 'success' : 'danger',
    accent: 'var(--success)',
    icon: 'dollar',
    progress: Math.min(projected / monthGoal, 1),
    progressColor: 'var(--success)',
  },
  {
    label: 'Achieved · MTD',
    primary: `$${(mtd / 1000).toFixed(0)}k`,
    sub: `through May ${MONTH_TODAY.day}`,
    accent: 'var(--gold)',
    icon: 'check-circ',
  },
  {
    label: 'Days remaining',
    primary: '8',
    sub: 'need $8.5k/day',
    subTone: 'danger',
    accent: 'var(--terracotta)',
    icon: 'stopwatch',
  },
  {
    label: 'New patients · MTD',
    primary: '64',
    sub: '▲ 12% vs Apr',
    subTone: 'success',
    accent: 'var(--ai-4)',
    icon: 'team',
  },
  {
    label: 'No-show rate',
    primary: '5.2%',
    sub: 'vs 3.5% last mo',
    subTone: 'danger',
    accent: 'var(--gold)',
    icon: 'alert',
  },
  {
    label: 'Cancellations',
    primary: '3.8%',
    sub: 'vs 2.9% last mo',
    subTone: 'danger',
    accent: 'var(--ai-3)',
    icon: 'clock',
  },
];

export const MONTH_INSIGHTS: InsightSection[] = [
  {
    tier: 'safety',
    title: 'Safety net',
    icon: 'shield',
    tone: 'red',
    items: [
      { icon: 'shield', text: 'Dr. Carter license renewal — due May 28' },
      { icon: 'doc', text: 'OSHA monthly log review — due May 31' },
    ],
  },
  {
    tier: 'blocker',
    title: 'Blockers',
    icon: 'alert',
    tone: 'red',
    items: [
      { icon: 'dollar', text: '$58k under goal — need $5.3k/day average to close' },
      { icon: 'heart', text: 'Hygiene reappt at 76% — 4 patients short of 80% goal' },
      { icon: 'doc', text: '8 unsigned treatment plans worth $24k' },
    ],
  },
  {
    tier: 'opportunity',
    title: 'Opportunities',
    icon: 'dollar',
    tone: 'gold',
    items: [
      { icon: 'dollar', text: '23 patients with unused YTD benefits over $1k each' },
      { icon: 'team', text: '12 on the unscheduled-tx list — $34k of potential' },
      { icon: 'heart', text: '5 patient referrals not yet contacted' },
    ],
  },
  {
    tier: 'nice',
    title: 'Nice to know',
    icon: 'sparkle',
    tone: 'ai',
    items: [
      { icon: 'crown', text: 'Bill Forrester · May 25 — 10-year patient milestone' },
      { icon: 'cake', text: '4 patient birthdays this month — 2 still to come' },
    ],
  },
];

export const MONTH_WEEKS: MonthWeekSummary[] = [
  { label: 'Week 1', range: 'May 1 – 2', production: 43674, fillPct: 88, days: 2, status: 'done' },
  { label: 'Week 2', range: 'May 4 – 9', production: 137900, fillPct: 84, days: 6, status: 'done' },
  {
    label: 'Week 3',
    range: 'May 11 – 16',
    production: 113800,
    fillPct: 71,
    days: 6,
    status: 'done',
    note: 'Sterilizer outage',
  },
  { label: 'Week 4', range: 'May 18 – 23', production: 97300, fillPct: 62, days: 6, status: 'current' },
  { label: 'Week 5', range: 'May 25 – 30', production: 116500, fillPct: 79, days: 6, status: 'projected' },
];

export const MONTH_HEADER_STATS = {
  projected: '$512k',
  mtd: '$356k',
  daysLeft: '8',
  newPatients: '64',
};

export const MONTH_TITLE = { month: 'May', year: '2026' };
